import React, { useState, useEffect, useRef } from 'react';
import { Tag } from '../../types/tag';
import { tagService } from '../../services/tagService';

// Custom simple icons to replace lucide-react
const X = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const Plus = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const Search = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const TagIcon = () => (
    <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
);

interface TagSelectorProps {
    selectedTags: number[];
    onChange: (tagIds: number[]) => void;
    placeholder?: string;
    allowCreate?: boolean;
    onTagsLoaded?: (tags: Tag[]) => void;
}

const TagSelector: React.FC<TagSelectorProps> = ({
    selectedTags,
    onChange,
    placeholder = "Select tags...",
    allowCreate = true,
    onTagsLoaded
}) => {
    const [tags, setTags] = useState<Tag[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadTags();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadTags = async () => {
        setIsLoading(true);
        try {
            const fetchedTags = await tagService.getTags();
            setTags(fetchedTags);
            if (onTagsLoaded) {
                onTagsLoaded(fetchedTags);
            }
        } catch (error) {
            console.error('Failed to load tags:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTag = async () => {
        if (!searchQuery.trim()) return;

        try {
            const newTag = await tagService.createTag({
                name: searchQuery.trim(),
                color: getRandomColor()
            });
            setTags([...tags, newTag]);
            onChange([...selectedTags, newTag.id]);
            setSearchQuery('');
        } catch (error) {
            console.error('Failed to create tag:', error);
        }
    };

    const toggleTag = (tagId: number) => {
        if (selectedTags.includes(tagId)) {
            onChange(selectedTags.filter(id => id !== tagId));
        } else {
            onChange([...selectedTags, tagId]);
        }
    };

    const getRandomColor = () => {
        const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899'];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const filteredTags = tags.filter(tag =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const selectedTagObjects = tags.filter(tag => selectedTags.includes(tag.id));

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                className="min-h-[42px] p-1.5 bg-snapfy-surface-hover border border-snapfy-border rounded-lg cursor-text flex flex-wrap gap-2 items-center"
                onClick={() => setIsOpen(true)}
            >
                {selectedTagObjects.length === 0 && !isOpen && (
                    <span className="text-snapfy-text-secondary ml-2 text-sm">{placeholder}</span>
                )}

                {selectedTagObjects.map(tag => (
                    <span
                        key={tag.id}
                        className="px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 text-white"
                        style={{ backgroundColor: tag.color + '40', border: `1px solid ${tag.color}` }}
                    >
                        {tag.name}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleTag(tag.id);
                            }}
                            className="hover:text-snapfy-text-primary/80"
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}

                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    className="bg-transparent border-none outline-none text-snapfy-text-primary text-sm flex-1 min-w-[60px] ml-1"
                    placeholder={selectedTagObjects.length === 0 ? "" : ""}
                />
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-snapfy-surface-hover border border-snapfy-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {isLoading ? (
                        <div className="p-4 text-center text-snapfy-text-secondary text-sm">Loading...</div>
                    ) : (
                        <>
                            {filteredTags.map(tag => (
                                <div
                                    key={tag.id}
                                    className="px-3 py-2 hover:bg-snapfy-inner cursor-pointer flex items-center justify-between"
                                    onClick={() => toggleTag(tag.id)}
                                >
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: tag.color }}
                                        />
                                        <span className="text-snapfy-text-primary text-sm">{tag.name}</span>
                                    </div>
                                    {selectedTags.includes(tag.id) && (
                                        <div className="w-2 h-2 bg-[#7063f7] rounded-full" />
                                    )}
                                </div>
                            ))}

                            {filteredTags.length === 0 && searchQuery && allowCreate && (
                                <div
                                    className="px-3 py-2 hover:bg-snapfy-inner cursor-pointer text-snapfy-accent-soft text-sm flex items-center gap-2"
                                    onClick={handleCreateTag}
                                >
                                    <Plus size={14} />
                                    Create "{searchQuery}"
                                </div>
                            )}

                            {filteredTags.length === 0 && !searchQuery && (
                                <div className="p-3 text-center text-snapfy-text-secondary text-sm">
                                    No tags found
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default TagSelector;
